import { useState, useCallback, useRef, useMemo, MutableRefObject } from 'react';
import { ScimEntityService } from '../../../services/ScimEntityService';
import CentralizedSchemaService from '../../../services/CentralizedSchemaService';
import { extractErrorMessage } from '../../../utils/apiUtils';
import type { BulkGroupItem, BulkGroupStatus, ProcessingStats, ConversionStats, FilterTestCache } from '../types/GroupsManagementTypes';
import { debugLog } from '../../../utils/debugLog';

interface UseBulkOperationsProps {
    entityServiceRef: MutableRefObject<ScimEntityService | undefined>;
    parseLegacyFilterWithErrorHandling: (text: string) => any;
    processFilterPipelineWithDeps: (filter: string) => Promise<string>;
    loadResponsibilityCenterCache: () => Promise<void>;
    batchResolveLegacyRcIds: (deps: any, ids: string[]) => Promise<Map<string, string>>;
    getRCResolutionDeps: () => any;
    cleanSetDisplayName: (name: string) => string;
    extractAttributesFromFilter: (filter: string) => string[];
    addUntranslatedRcIds: (ids: string[]) => void;
    rcNotFoundCacheRef: MutableRefObject<Set<string>>;
    setLegacySuccess: (msg: string | null) => void;
    setLegacyError: (msg: string | null) => void;
}

/**
 * Custom hook for managing bulk group operations (loading, processing, converting)
 */
export function useBulkOperations({
    entityServiceRef,
    parseLegacyFilterWithErrorHandling,
    processFilterPipelineWithDeps,
    loadResponsibilityCenterCache,
    batchResolveLegacyRcIds,
    getRCResolutionDeps,
    cleanSetDisplayName,
    extractAttributesFromFilter,
    addUntranslatedRcIds,
    rcNotFoundCacheRef,
    setLegacySuccess,
    setLegacyError
}: UseBulkOperationsProps) {

    // ========== State Management ==========

    const [bulkGroups, setBulkGroups] = useState<BulkGroupItem[]>([]);
    const [selectedBulkGroups, setSelectedBulkGroups] = useState<Set<string>>(new Set());
    const [bulkGroupsPage, setBulkGroupsPage] = useState(0);
    const [bulkGroupsRowsPerPage, setBulkGroupsRowsPerPage] = useState(40);
    const [bulkGroupsTotalResults, setBulkGroupsTotalResults] = useState<number | null>(null);
    const [loadingBulkGroups, setLoadingBulkGroups] = useState(false);
    const [processingBulkGroups, setProcessingBulkGroups] = useState(false);

    // Stats
    const [processingStats, setProcessingStats] = useState<ProcessingStats | null>(null);
    const [conversionStats, setConversionStats] = useState<ConversionStats | null>(null);

    // Status tracking
    const [bulkGroupStatuses, setBulkGroupStatuses] = useState<Map<string, BulkGroupStatus>>(new Map());

    // Edited filters
    const [editingConvertedFilter, setEditingConvertedFilter] = useState<string | null>(null);
    const [editedConvertedFilters, setEditedConvertedFilters] = useState<Map<string, string>>(new Map());

    // Sub-tab (Groups vs Raw Views)
    const [bulkSubTab, setBulkSubTab] = useState(0);

    // Filter toggles
    const [excludeManaged, setExcludeManaged] = useState(true);
    const [excludeHasFilter, setExcludeHasFilter] = useState(true);
    const [excludeJobCodeMemberOf, setExcludeJobCodeMemberOf] = useState(true);
    const [bulkCustomFilter, setBulkCustomFilter] = useState<string>('');

    // Refs
    const bulkGroupsLoadedRef = useRef(false);
    const processingAbortControllerRef = useRef<AbortController | null>(null);
    const loadBulkGroupsAbortControllerRef = useRef<AbortController | null>(null);
    const filterTestCacheRef = useRef<Map<string, FilterTestCache>>(new Map());

    const schemaService = CentralizedSchemaService.getInstance();

    // ========== Load Bulk Groups ==========

    const loadBulkGroups = useCallback(async (
        page: number,
        pageSize: number,
        excludeManagedGroups?: boolean,
        excludeHasFilterGroups?: boolean,
        excludeJobCodeMemberOfGroups?: boolean,
        customFilter?: string
    ) => {
        // Cancel any previous request
        if (loadBulkGroupsAbortControllerRef.current) {
            loadBulkGroupsAbortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        loadBulkGroupsAbortControllerRef.current = abortController;

        setLoadingBulkGroups(true);

        try {
            // Build filter
            let filterParts: string[] = ['(LegacyFilter pr)'];

            if (excludeHasFilterGroups !== undefined ? excludeHasFilterGroups : excludeHasFilter) {
                filterParts.push('(not(Filter pr))');
            }

            if (excludeManagedGroups !== undefined ? excludeManagedGroups : excludeManaged) {
                filterParts.push('(not(Managed eq true))');
            }

            filterParts.push('(not(LegacyFilter co "MediaSpecialist"))');

            if (excludeJobCodeMemberOfGroups !== undefined ? excludeJobCodeMemberOfGroups : excludeJobCodeMemberOf) {
                filterParts.push('(not(LegacyFilter co "JobCodeMemberOf"))');
            }

            if (customFilter !== undefined ? customFilter : bulkCustomFilter) {
                const customFilterValue = customFilter !== undefined ? customFilter : bulkCustomFilter;
                if (customFilterValue.trim()) {
                    filterParts.push(`(${customFilterValue.trim()})`);
                }
            }

            const filter = filterParts.join(' and ');

            const response = await entityServiceRef.current!.searchGroups({
                filter: filter,
                page: page + 1,
                pageSize: pageSize,
                attributes: ['LegacyFilter', 'Managed', 'Filter', 'FilterScope', 'Member'],
                abortSignal: abortController.signal
            });

            setBulkGroupsTotalResults(response.totalResults || 0);

            // Start processing timer
            const processingStartTime = performance.now();
            let itemsNotInCache = 0;

            // Fetch sets
            let setsWithFilters: any[] = [];
            try {
                const setsResponse = await entityServiceRef.current!.getEntitiesByType('Set', 1, 100, undefined, 'Filter pr', undefined, abortController.signal);
                setsWithFilters = setsResponse.data || [];
            } catch (setErr) {
                if (abortController.signal.aborted) throw setErr;
                console.warn('Error fetching sets with filters:', setErr);
            }

            let allSets: any[] = [];
            try {
                const allSetsResponse = await entityServiceRef.current!.getEntitiesByType('Set', 1, 100, undefined, undefined, undefined, abortController.signal);
                allSets = allSetsResponse.data || [];
            } catch (setErr) {
                if (abortController.signal.aborted) throw setErr;
                console.warn('Error fetching all sets:', setErr);
            }

            const setFiltersMap = new Map<string, boolean>();
            setsWithFilters.forEach((set: any) => {
                const filter = set.Filter || set.filter;
                if (filter) {
                    const normalizedFilter = filter.trim().replace(/\s+/g, ' ');
                    setFiltersMap.set(normalizedFilter, true);
                }
            });

            const normalizedSetNames: string[] = [];
            allSets.forEach((set: any) => {
                const displayName = set.DisplayName || set.displayName;
                if (displayName) {
                    normalizedSetNames.push(displayName.trim().toLowerCase());
                }
            });

            // Load Person schema
            let personSchema: any = null;
            try {
                const schemaData = await schemaService.getSchemaData();
                personSchema = schemaData.schemas.find((schema: any) =>
                    schema.name?.toLowerCase() === 'person'
                );
            } catch (schemaErr) {
                console.warn('Could not load schema for attribute validation:', schemaErr);
            }

            const personAttributeNames = personSchema
                ? new Set((personSchema.attributes || []).map((attr: any) => attr.name?.toLowerCase()))
                : new Set<string>();

            // Load RC cache
            await loadResponsibilityCenterCache();

            // FIRST PASS: Parse all legacy filters and extract RC IDs
            const allLegacyRcIds = new Set<string>();
            const parsedFiltersMap = new Map<string, { entityType: string; filter: string } | null>();

            for (const group of ((response.groups || []) as any[])) {
                const groupId = group.Id || group.id;
                const legacyFilterValue = group.LegacyFilter || group.legacyFilter || '';
                if (legacyFilterValue) {
                    try {
                        const parsed = parseLegacyFilterWithErrorHandling(legacyFilterValue);
                        parsedFiltersMap.set(groupId, parsed);

                        if (parsed && parsed.filter) {
                            const rcPattern = /\b(Rc|Rcs|PrincipalOf)\s+(eq|ne|co|sw|ew|pr|gt|ge|lt|le)\s+["']([^"']+)["']/gi;
                            const matches = Array.from(parsed.filter.matchAll(rcPattern));
                            for (const match of matches) {
                                const legacyId = (match as RegExpMatchArray)[3];
                                if (legacyId) {
                                    allLegacyRcIds.add(legacyId);
                                }
                            }
                        }
                    } catch (err) {
                        parsedFiltersMap.set(groupId, null);
                        console.warn(`Failed to parse legacy filter for group ${groupId}:`, err);
                    }
                } else {
                    parsedFiltersMap.set(groupId, null);
                }
            }

            // Batch resolve RC IDs
            if (allLegacyRcIds.size > 0) {
                const rcDeps = getRCResolutionDeps();
                await batchResolveLegacyRcIds(rcDeps, Array.from(allLegacyRcIds));
                itemsNotInCache = allLegacyRcIds.size;
            }

            // Create initial groups array
            const initialGroups = ((response.groups || []) as any[]).map((group: any) => {
                const legacyFilterValue = group.LegacyFilter || group.legacyFilter || '';
                const isManaged = group.Managed || group.managed || group.IsManaged || group.isManaged || false;
                const filterValue = group.Filter || group.filter || '';
                const filterScope = group.FilterScope || group.filterScope || '';
                const groupDisplayName = group.DisplayName || group.displayName || group.Id || group.id || 'Unknown';
                const hasFilter = filterValue && filterValue.trim() !== '';
                const isManagedWithFilter = isManaged && hasFilter;
                const member = group.Member || group.member || [];

                return {
                    id: group.Id || group.id,
                    displayName: groupDisplayName,
                    legacyFilter: legacyFilterValue,
                    convertedFilter: '',
                    optimizedFilter: '',
                    needsFilterUpdate: false,
                    hasSet: false,
                    filterMatch: filterValue,
                    filterScope: filterScope,
                    member: member,
                    nameMatch: false,
                    nameWarning: undefined,
                    attributeWarning: undefined,
                    isManaged,
                    filterTestWarning: isManagedWithFilter ? 'Testing...' : undefined,
                    filterTestGroupCount: undefined,
                    existingFilterCount: undefined,
                    optimizedFilterCount: undefined,
                    _loading: {
                        converted: !!legacyFilterValue,
                        optimized: !!legacyFilterValue,
                        counts: false
                    }
                };
            });

            setBulkGroups(initialGroups);

            // SECOND PASS: Process each group with filter conversion
            const filterTestsToRun: Array<{ groupId: string; filterScope: string; filter: string; type: 'existing' | 'optimized' }> = [];

            for (let i = 0; i < initialGroups.length; i++) {
                const group = initialGroups[i];
                const groupId = group.id;
                const groupDisplayName = group.displayName;
                const parsed = parsedFiltersMap.get(groupId);

                if (!parsed) continue;

                const entityType = parsed.entityType;
                let convertedFilter = parsed.filter;
                let optimizedFilter = '';
                let attributeWarning: string[] | undefined = undefined;
                let needsFilterUpdate = false;
                let existingFilterCount: number | undefined = undefined;
                let optimizedFilterCount: number | undefined = undefined;

                try {
                    // Mark as starting conversion
                    setBulkGroups(prev => prev.map(g => {
                        if (g.id === groupId) {
                            return { ...g, _loading: { ...g._loading, converted: true, optimized: true } };
                        }
                        return g;
                    }));

                    optimizedFilter = await processFilterPipelineWithDeps(parsed.filter);

                    // Clear loading flags
                    setBulkGroups(prev => prev.map(g => {
                        if (g.id === groupId) {
                            return { ...g, _loading: { ...g._loading, converted: false, optimized: false } };
                        }
                        return g;
                    }));

                    // Check if filter needs updating
                    const filterValue = group.filterMatch || '';
                    if (filterValue && filterValue.trim() !== '') {
                        const normalizeForComparison = (f: string): string => {
                            return f.trim().replace(/\s+/g, ' ').toLowerCase();
                        };

                        const normalizedExisting = normalizeForComparison(filterValue);
                        const normalizedOptimized = normalizeForComparison(optimizedFilter);

                        if (normalizedExisting !== normalizedOptimized) {
                            needsFilterUpdate = true;

                            // Queue filter tests for both existing and optimized
                            const filterScope = group.filterScope || entityType;
                            if (filterScope) {
                                filterTestsToRun.push({
                                    groupId: groupId,
                                    filterScope: filterScope,
                                    filter: filterValue,
                                    type: 'existing'
                                });
                                filterTestsToRun.push({
                                    groupId: groupId,
                                    filterScope: filterScope,
                                    filter: optimizedFilter,
                                    type: 'optimized'
                                });
                                existingFilterCount = undefined;
                                optimizedFilterCount = undefined;
                            }
                        } else {
                            needsFilterUpdate = false;

                            // Filters match, but still test the optimized filter to get count
                            const filterScope = group.filterScope || entityType;
                            if (filterScope) {
                                filterTestsToRun.push({
                                    groupId: groupId,
                                    filterScope: filterScope,
                                    filter: optimizedFilter,
                                    type: 'optimized'
                                });
                                optimizedFilterCount = undefined;
                            }
                        }
                    }

                    // Check for untranslated RC values
                    if (optimizedFilter) {
                        const rcPattern = /\b(Rc|Rcs|PrincipalOf)\s+(eq|ne|co|sw|ew|pr|gt|ge|lt|le)\s+["']([^"']+)["']/gi;
                        const originalMatches = Array.from(parsed.filter.matchAll(rcPattern));
                        const optimizedMatches = Array.from(optimizedFilter.matchAll(rcPattern));

                        const originalValues = new Set<string>();
                        for (const match of originalMatches) {
                            originalValues.add(match[3]);
                        }

                        const untranslatedRcs: string[] = [];
                        for (const match of optimizedMatches) {
                            const value = match[3];
                            if (originalValues.has(value)) {
                                const is4Digit = /^\d{4}$/.test(value);
                                const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
                                const wasNotFound = rcNotFoundCacheRef.current.has(value);

                                if ((is4Digit || (isGuid && wasNotFound))) {
                                    untranslatedRcs.push(value);
                                }
                            }
                        }

                        if (untranslatedRcs.length > 0) {
                            const uniqueRcs = Array.from(new Set(untranslatedRcs));
                            if (!attributeWarning) {
                                attributeWarning = [];
                            }
                            attributeWarning.push(`Untranslated RC IDs: ${uniqueRcs.join(', ')}`);
                            addUntranslatedRcIds(uniqueRcs);
                        }
                    }

                    // Check for missing attributes (Person only)
                    if (entityType?.toLowerCase() === 'person' && personAttributeNames.size > 0) {
                        const attributes = extractAttributesFromFilter(parsed.filter);
                        const missingAttributes = attributes.filter(attr =>
                            !personAttributeNames.has(attr.toLowerCase())
                        );

                        if (missingAttributes.length > 0) {
                            if (!attributeWarning) {
                                attributeWarning = [];
                            }
                            attributeWarning.push(`Missing attributes: ${missingAttributes.join(', ')}`);
                        }
                    }
                } catch (convErr) {
                    console.warn('Error running conversion pipeline for group:', groupDisplayName, convErr);
                    optimizedFilter = convertedFilter;
                    setBulkGroups(prev => prev.map(g => {
                        if (g.id === groupId) {
                            return { ...g, _loading: { ...g._loading, converted: false, optimized: false } };
                        }
                        return g;
                    }));
                }

                // Check name match
                let nameMatch = false;
                if (groupDisplayName) {
                    const normalizedGroupName = groupDisplayName.trim().toLowerCase();
                    nameMatch = normalizedSetNames.some(setName => setName.startsWith(normalizedGroupName));
                }

                const hasSet = false;
                const filterValue = group.filterMatch || '';
                const hasFilter = filterValue && filterValue.trim() !== '';
                const isManagedWithFilter = group.isManaged && hasFilter;
                const filterScope = group.filterScope || '';
                const hasCountTestsQueued = filterTestsToRun.some(test => test.groupId === groupId);

                // Update group data
                const groupData = {
                    id: group.id,
                    displayName: groupDisplayName,
                    legacyFilter: group.legacyFilter,
                    convertedFilter: convertedFilter,
                    optimizedFilter: optimizedFilter,
                    needsFilterUpdate: needsFilterUpdate,
                    existingFilterCount: needsFilterUpdate ? existingFilterCount : undefined,
                    optimizedFilterCount: optimizedFilterCount,
                    hasSet,
                    filterMatch: filterValue,
                    filterScope: filterScope,
                    nameMatch,
                    nameWarning: undefined,
                    attributeWarning,
                    isManaged: group.isManaged,
                    filterTestWarning: isManagedWithFilter ? 'Testing...' : undefined,
                    member: group.member,
                    _loading: {
                        converted: false,
                        optimized: false,
                        counts: hasCountTestsQueued || (needsFilterUpdate && (existingFilterCount === undefined || optimizedFilterCount === undefined))
                    }
                };

                // Update in bulk groups
                setBulkGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...groupData } : g));
            }

            // Run filter tests in batches
            const batchSize = 5;
            for (let i = 0; i < filterTestsToRun.length; i += batchSize) {
                const batch = filterTestsToRun.slice(i, i + batchSize);

                await Promise.all(batch.map(async (test) => {
                    const cacheKey = `${test.filterScope}:${test.filter}`;
                    const cachedResult = filterTestCacheRef.current.get(cacheKey);

                    if (cachedResult !== undefined) {
                        setBulkGroups(prev => prev.map(g => {
                            if (g.id === test.groupId) {
                                if (test.type === 'existing') {
                                    return { ...g, existingFilterCount: cachedResult.count };
                                } else {
                                    return { ...g, optimizedFilterCount: cachedResult.count, filterTestWarning: cachedResult.error };
                                }
                            }
                            return g;
                        }));
                        return;
                    }

                    try {
                        const countResponse = await entityServiceRef.current!.getEntitiesByType(
                            test.filterScope,
                            1,
                            2,
                            undefined,
                            test.filter,
                            undefined,
                            abortController.signal
                        );

                        const count = countResponse.totalResults || 0;
                        filterTestCacheRef.current.set(cacheKey, { count });

                        setBulkGroups(prev => prev.map(g => {
                            if (g.id === test.groupId) {
                                if (test.type === 'existing') {
                                    return { ...g, existingFilterCount: count, _loading: { ...g._loading, counts: false } };
                                } else {
                                    return { ...g, optimizedFilterCount: count, filterTestWarning: count === 0 ? 'Count is 0' : undefined, _loading: { ...g._loading, counts: false } };
                                }
                            }
                            return g;
                        }));
                    } catch (err: any) {
                        if (abortController.signal.aborted) return;

                        const errorMsg = extractErrorMessage(err);
                        filterTestCacheRef.current.set(cacheKey, { error: errorMsg });

                        setBulkGroups(prev => prev.map(g => {
                            if (g.id === test.groupId) {
                                if (test.type === 'optimized') {
                                    return { ...g, filterTestWarning: errorMsg, _loading: { ...g._loading, counts: false } };
                                }
                            }
                            return g;
                        }));
                    }
                }));
            }

            // End processing timer
            const processingEndTime = performance.now();
            const processingTimeMs = processingEndTime - processingStartTime;

            setProcessingStats({
                processingTimeMs,
                itemsProcessed: initialGroups.length,
                itemsNotInCache
            });

        } catch (err: any) {
            if (err.name !== 'AbortError' && !abortController.signal.aborted) {
                console.error('Error loading bulk groups:', err);
                setLegacyError(extractErrorMessage(err));
            }
        } finally {
            if (loadBulkGroupsAbortControllerRef.current === abortController) {
                loadBulkGroupsAbortControllerRef.current = null;
            }
            setLoadingBulkGroups(false);
        }
    }, [
        entityServiceRef,
        excludeManaged,
        excludeHasFilter,
        excludeJobCodeMemberOf,
        bulkCustomFilter,
        parseLegacyFilterWithErrorHandling,
        processFilterPipelineWithDeps,
        loadResponsibilityCenterCache,
        batchResolveLegacyRcIds,
        getRCResolutionDeps,
        extractAttributesFromFilter,
        addUntranslatedRcIds,
        rcNotFoundCacheRef,
        setLegacyError,
        schemaService,
        cleanSetDisplayName
    ]);

    // ========== Selection Handlers ==========

    const handleSelectBulkGroup = useCallback((groupId: string, selected: boolean) => {
        setSelectedBulkGroups(prev => {
            const newSet = new Set(prev);
            if (selected) {
                newSet.add(groupId);
            } else {
                newSet.delete(groupId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAllBulkGroups = useCallback((selected: boolean) => {
        if (selected) {
            const selectableGroups = bulkGroups.filter(g => {
                // Select groups where:
                // - count is 0 or higher, OR
                // - count test failed (filterTestWarning exists and is an error, not just warning about count)
                const hasValidCount = g.optimizedFilterCount !== undefined;
                const hasError = g.filterTestWarning && !g.filterTestWarning.includes('Count is 0') && g.filterTestWarning !== 'Testing...';
                return hasValidCount || hasError;
            });
            setSelectedBulkGroups(new Set(selectableGroups.map(g => g.id)));
        } else {
            setSelectedBulkGroups(new Set());
        }
    }, [bulkGroups]);

    // ========== Process Bulk Groups ==========

    const handleProcessBulkGroups = useCallback(async () => {
        if (selectedBulkGroups.size === 0) {
            setLegacyError('Please select at least one group to process');
            return;
        }

        const conversionStartTime = performance.now();
        setConversionStats(null);

        const abortController = new AbortController();
        processingAbortControllerRef.current = abortController;

        setProcessingBulkGroups(true);
        setBulkGroupStatuses(new Map());

        const selectedGroups = bulkGroups.filter(g => selectedBulkGroups.has(g.id));
        let itemsConverted = 0;

        try {
            for (const group of selectedGroups) {
                if (abortController.signal.aborted) break;

                if (!group.legacyFilter) {
                    setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                        status: 'error',
                        message: 'No LegacyFilter found'
                    }));
                    continue;
                }

                try {
                    setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                        status: 'processing',
                        message: 'Processing...'
                    }));

                    const parsed = parseLegacyFilterWithErrorHandling(group.legacyFilter);
                    if (!parsed) {
                        setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                            status: 'error',
                            message: 'Failed to parse legacy filter'
                        }));
                        continue;
                    }

                    let filterToUse = editedConvertedFilters.get(group.id);
                    if (!filterToUse) {
                        filterToUse = await processFilterPipelineWithDeps(parsed.filter);
                    }

                    // Delete any associated sets if needed
                    if (group.hasSet) {
                        try {
                            const setDisplayName = cleanSetDisplayName(`${group.displayName}-Legacy`);
                            const setsResponse = await entityServiceRef.current!.getEntitiesByType('Set', 1, 100, undefined, `DisplayName eq "${setDisplayName}"`, undefined, abortController.signal);
                            const matchingSets = setsResponse.data || [];

                            for (const set of matchingSets) {
                                if (abortController.signal.aborted) break;
                                const setName = set.DisplayName || set.displayName || '';
                                if (setName.toLowerCase().startsWith(group.displayName.toLowerCase())) {
                                    try {
                                        await entityServiceRef.current!.deleteEntity('Set', set.Id || set.id);
                                    } catch (deleteErr: any) {
                                        if (deleteErr.name === 'AbortError' || abortController.signal.aborted) break;
                                        console.warn(`Failed to delete set ${setName}:`, deleteErr);
                                    }
                                }
                            }
                        } catch (setSearchErr: any) {
                            if (setSearchErr.name === 'AbortError' || abortController.signal.aborted) break;
                            console.warn('Error searching for sets to delete:', setSearchErr);
                        }
                    }

                    if (abortController.signal.aborted) break;

                    // Build patch operations
                    const patchOperations: Array<{ op: string; path: string; value: any }> = [];

                    const currentFilter = group.filterMatch || '';
                    const normalizeFilter = (f: string) => f.trim().replace(/\s+/g, ' ').toLowerCase();
                    if (normalizeFilter(currentFilter) !== normalizeFilter(filterToUse)) {
                        patchOperations.push({
                            op: 'replace',
                            path: 'Filter',
                            value: filterToUse
                        });
                    }

                    const currentFilterScope = group.filterScope || '';
                    if (currentFilterScope !== parsed.entityType) {
                        patchOperations.push({
                            op: 'replace',
                            path: 'FilterScope',
                            value: parsed.entityType
                        });
                    }

                    // Only set Managed if count >= 1
                    const countForManaged = group.optimizedFilterCount ?? group.filterTestGroupCount;
                    const totalResultsForManaged = countForManaged ?? 0;
                    const currentManaged = group.isManaged || false;

                    if (!currentManaged && totalResultsForManaged >= 1) {
                        patchOperations.push({
                            op: 'replace',
                            path: 'Managed',
                            value: true
                        });
                    }

                    if (patchOperations.length === 0) {
                        setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                            status: 'warning',
                            message: 'No changes needed'
                        }));
                        continue;
                    }

                    // Patch the group
                    const patchRequest = {
                        schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
                        Operations: patchOperations
                    };
                    const patchResponse = await entityServiceRef.current!.patchEntity(group.id, patchRequest, 'Group');

                    // Update state
                    const updatedFilterMatch = filterToUse;
                    const updatedFilterScope = parsed.entityType;
                    const wasManaged = totalResultsForManaged >= 1;

                    setBulkGroups(prev => prev.map(g => {
                        if (g.id === group.id) {
                            return {
                                ...g,
                                filterMatch: updatedFilterMatch,
                                filterScope: updatedFilterScope,
                                isManaged: wasManaged || g.isManaged,
                                needsFilterUpdate: false,
                                optimizedFilter: updatedFilterMatch
                            };
                        }
                        return g;
                    }));

                    setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                        status: 'success',
                        message: `Updated successfully`
                    }));

                    itemsConverted++;

                } catch (err: any) {
                    if (err.name === 'AbortError' || abortController.signal.aborted) {
                        setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                            status: 'warning',
                            message: 'Cancelled'
                        }));
                        break;
                    }

                    const errorMessage = extractErrorMessage(err);
                    setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                        status: 'error',
                        message: errorMessage
                    }));
                }
            }
        } catch (abortErr: any) {
            if (abortErr.name === 'AbortError' || abortController.signal.aborted) {
                debugLog('Bulk processing cancelled by user');
            } else {
                throw abortErr;
            }
        } finally {
            const conversionEndTime = performance.now();
            const conversionTimeMs = conversionEndTime - conversionStartTime;

            setConversionStats({
                conversionTimeMs,
                itemsConverted
            });

            if (processingAbortControllerRef.current === abortController) {
                processingAbortControllerRef.current = null;
            }
            setProcessingBulkGroups(false);
            if (!abortController.signal.aborted) {
                setSelectedBulkGroups(new Set());
                setLegacySuccess(`Processed ${selectedGroups.length} group(s)`);

                // Clear any stuck 'processing' statuses
                setBulkGroupStatuses(prev => {
                    const newMap = new Map(prev);
                    prev.forEach((status, groupId) => {
                        if (status.status === 'processing') {
                            newMap.set(groupId, {
                                status: 'success',
                                message: 'Completed'
                            });
                        }
                    });
                    return newMap;
                });
            }
        }
    }, [
        selectedBulkGroups,
        bulkGroups,
        parseLegacyFilterWithErrorHandling,
        processFilterPipelineWithDeps,
        editedConvertedFilters,
        cleanSetDisplayName,
        entityServiceRef,
        setLegacyError,
        setLegacySuccess
    ]);

    // ========== Other Handlers ==========

    const handleCancelBulkProcessing = useCallback(() => {
        if (processingAbortControllerRef.current) {
            processingAbortControllerRef.current.abort();
            processingAbortControllerRef.current = null;
        }
        setProcessingBulkGroups(false);
        setBulkGroupStatuses(prev => {
            const newMap = new Map(prev);
            prev.forEach((status, groupId) => {
                if (status.status === 'processing') {
                    newMap.set(groupId, {
                        status: 'warning',
                        message: 'Cancelled'
                    });
                }
            });
            return newMap;
        });
    }, []);

    const handleReloadBulkGroups = useCallback(async () => {
        setLoadingBulkGroups(true);
        setBulkGroups([]);
        setSelectedBulkGroups(new Set());
        setProcessingStats(null);
        setConversionStats(null);
        setLegacySuccess(null);
        setLegacyError(null);
        filterTestCacheRef.current.clear();
        rcNotFoundCacheRef.current.clear();
        await loadBulkGroups(bulkGroupsPage, bulkGroupsRowsPerPage, excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter);
    }, [loadBulkGroups, bulkGroupsPage, bulkGroupsRowsPerPage, excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter, rcNotFoundCacheRef, setLegacySuccess, setLegacyError]);

    // Update filters for selected groups that need updates (without full conversion)
    const handleUpdateFilters = useCallback(async () => {
        const groupsNeedingUpdate = bulkGroups.filter(g =>
            selectedBulkGroups.has(g.id) && g.needsFilterUpdate && g.optimizedFilter
        );

        if (groupsNeedingUpdate.length === 0) {
            setLegacyError('No selected groups need filter updates');
            return;
        }

        const abortController = new AbortController();
        processingAbortControllerRef.current = abortController;

        setProcessingBulkGroups(true);
        setBulkGroupStatuses(new Map());

        try {
            for (const group of groupsNeedingUpdate) {
                if (abortController.signal.aborted) {
                    break;
                }

                try {
                    setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                        status: 'processing',
                        message: 'Updating filter...'
                    }));

                    // Get the full group to check FilterScope
                    const fullGroup = await entityServiceRef.current!.getEntityById(
                        group.id,
                        'Group',
                        true
                    );

                    const filterScope = fullGroup?.FilterScope || fullGroup?.filterScope;
                    if (!filterScope) {
                        setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                            status: 'error',
                            message: 'Missing FilterScope'
                        }));
                        continue;
                    }

                    // Update just the Filter property
                    const patchOperations = [
                        {
                            op: 'replace',
                            path: 'Filter',
                            value: group.optimizedFilter!
                        }
                    ];

                    const patchRequest = {
                        schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
                        Operations: patchOperations
                    };

                    await entityServiceRef.current!.patchEntity(group.id, patchRequest, 'Group', abortController.signal);

                    setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                        status: 'success',
                        message: 'Filter updated'
                    }));

                    // Update the group in state to reflect the change
                    const updatedFilter = group.optimizedFilter!;
                    const preservedCount = group.optimizedFilterCount;
                    setBulkGroups(prev => prev.map(g =>
                        g.id === group.id
                            ? {
                                ...g,
                                filterMatch: updatedFilter,
                                convertedFilter: updatedFilter,
                                optimizedFilter: updatedFilter,
                                needsFilterUpdate: false,
                                existingFilterCount: undefined,
                                optimizedFilterCount: preservedCount,
                                _loading: {
                                    ...g._loading,
                                    counts: false
                                }
                            }
                            : g
                    ));

                } catch (err: any) {
                    if (err.name === 'AbortError' || abortController.signal.aborted) {
                        break;
                    }
                    const errorMessage = err instanceof Error ? err.message : 'Failed to update filter';
                    setBulkGroupStatuses(prev => new Map(prev).set(group.id, {
                        status: 'error',
                        message: errorMessage
                    }));
                }
            }

            setLegacySuccess(`Updated filters for ${groupsNeedingUpdate.length} group(s)`);
        } catch (err) {
            console.error('Error updating filters:', err);
            setLegacyError(err instanceof Error ? err.message : 'Failed to update filters');
        } finally {
            setProcessingBulkGroups(false);
            processingAbortControllerRef.current = null;
        }
    }, [selectedBulkGroups, bulkGroups, entityServiceRef, setLegacySuccess, setLegacyError]);

    const handleBulkSearch = useCallback(async () => {
        setBulkGroupsPage(0);
        setLoadingBulkGroups(true);
        setBulkGroups([]);
        setSelectedBulkGroups(new Set());
        setProcessingStats(null);
        setConversionStats(null);
        setLegacySuccess(null);
        setLegacyError(null);
        filterTestCacheRef.current.clear();
        rcNotFoundCacheRef.current.clear();
        await loadBulkGroups(0, bulkGroupsRowsPerPage, excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter);
    }, [loadBulkGroups, bulkGroupsRowsPerPage, excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter, rcNotFoundCacheRef, setLegacySuccess, setLegacyError]);

    const effectiveFilter = useMemo(() => {
        const filterParts: string[] = ['(LegacyFilter pr)'];

        if (excludeHasFilter) {
            filterParts.push('(not(Filter pr))');
        }

        if (excludeManaged) {
            filterParts.push('(not(Managed eq true))');
        }

        filterParts.push('(not(LegacyFilter co "MediaSpecialist"))');

        if (excludeJobCodeMemberOf) {
            filterParts.push('(not(LegacyFilter co "JobCodeMemberOf"))');
        }

        if (bulkCustomFilter.trim()) {
            filterParts.push(`(${bulkCustomFilter.trim()})`);
        }

        return filterParts.join(' and ');
    }, [excludeManaged, excludeHasFilter, excludeJobCodeMemberOf, bulkCustomFilter]);

    const handleRefreshRow = useCallback(async (groupId: string) => {
        // Find the group
        const group = bulkGroups.find(g => g.id === groupId);
        if (!group) return;

        // Re-run filter test for this row
        const filterScope = group.filterScope;
        const optimizedFilter = group.optimizedFilter;

        if (!filterScope || !optimizedFilter) return;

        // Mark as loading
        setBulkGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return { ...g, _loading: { ...g._loading, counts: true } };
            }
            return g;
        }));

        try {
            const countResponse = await entityServiceRef.current!.getEntitiesByType(
                filterScope,
                1,
                2,
                undefined,
                optimizedFilter,
                undefined
            );

            const count = countResponse.totalResults || 0;

            setBulkGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return {
                        ...g,
                        optimizedFilterCount: count,
                        filterTestWarning: count === 0 ? 'Count is 0' : undefined,
                        _loading: { ...g._loading, counts: false }
                    };
                }
                return g;
            }));
        } catch (err: any) {
            const errorMsg = extractErrorMessage(err);
            setBulkGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return {
                        ...g,
                        filterTestWarning: errorMsg,
                        _loading: { ...g._loading, counts: false }
                    };
                }
                return g;
            }));
        }
    }, [bulkGroups, entityServiceRef]);

    // Handle edited filter blur - test the edited filter and update counts
    const handleEditedFilterBlur = useCallback(async (groupId: string) => {
        const group = bulkGroups.find(g => g.id === groupId);
        if (!group) {
            setEditingConvertedFilter(null);
            return;
        }

        const editedFilter = editedConvertedFilters.get(groupId);
        const filterScope = group.filterScope;

        // If no changes or no filter scope, just close edit mode
        if (!editedFilter || !filterScope) {
            setEditingConvertedFilter(null);
            return;
        }

        // Mark as loading
        setBulkGroups(prev => prev.map(g => {
            if (g.id === groupId) {
                return { ...g, _loading: { ...g._loading, counts: true } };
            }
            return g;
        }));

        try {
            const cacheKey = `${filterScope}:${editedFilter}`;
            const cachedResult = filterTestCacheRef.current.get(cacheKey);

            let count: number | undefined;
            let errorMessage: string | undefined;

            if (cachedResult && (cachedResult.count === 0 || cachedResult.error)) {
                // Use cached result
                count = cachedResult.count ?? 0;
                errorMessage = cachedResult.error;
            } else {
                // Test the filter
                const testResponse = await entityServiceRef.current!.getEntitiesByType(
                    filterScope,
                    1,
                    2,
                    undefined,
                    editedFilter
                );
                count = testResponse.totalResults || 0;
                filterTestCacheRef.current.set(cacheKey, { count });
            }

            // Update the group with the new count and edited filter
            setBulkGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return {
                        ...g,
                        optimizedFilter: editedFilter, // Update to the edited filter
                        optimizedFilterCount: count,
                        filterTestGroupCount: count,
                        filterTestWarning: errorMessage || (count === 0 ? 'Count is 0' : undefined),
                        _loading: { ...g._loading, counts: false }
                    };
                }
                return g;
            }));
        } catch (err) {
            const errorMessage = extractErrorMessage(err);
            const cacheKey = `${filterScope}:${editedFilter}`;
            filterTestCacheRef.current.set(cacheKey, { error: errorMessage });

            setBulkGroups(prev => prev.map(g => {
                if (g.id === groupId) {
                    return {
                        ...g,
                        filterTestWarning: errorMessage,
                        _loading: { ...g._loading, counts: false }
                    };
                }
                return g;
            }));
        }

        setEditingConvertedFilter(null);
    }, [bulkGroups, editedConvertedFilters, entityServiceRef]);

    // ========== Return Hook API ==========

    return {
        // State
        bulkGroups,
        setBulkGroups,
        selectedBulkGroups,
        setSelectedBulkGroups,
        bulkGroupsPage,
        setBulkGroupsPage,
        bulkGroupsRowsPerPage,
        setBulkGroupsRowsPerPage,
        bulkGroupsTotalResults,
        loadingBulkGroups,
        processingBulkGroups,
        processingStats,
        conversionStats,
        bulkGroupStatuses,
        editingConvertedFilter,
        setEditingConvertedFilter,
        editedConvertedFilters,
        setEditedConvertedFilters,
        bulkSubTab,
        setBulkSubTab,
        excludeManaged,
        setExcludeManaged,
        excludeHasFilter,
        setExcludeHasFilter,
        excludeJobCodeMemberOf,
        setExcludeJobCodeMemberOf,
        bulkCustomFilter,
        setBulkCustomFilter,
        effectiveFilter,
        filterTestCacheRef,

        // Functions
        loadBulkGroups,
        handleSelectBulkGroup,
        handleSelectAllBulkGroups,
        handleProcessBulkGroups,
        handleCancelBulkProcessing,
        handleReloadBulkGroups,
        handleUpdateFilters,
        handleBulkSearch,
        handleRefreshRow,
        handleEditedFilterBlur
    };
}

